import { BehaviorSubject } from 'rxjs';
import { getRandomString, useObservable } from './U';
import { useEffect, useRef } from 'react';
export enum TransitionType {
  button,
  invoke,
  scroll,
}

export class UIState {
  name: string;
  private transitions: Transition[] = [];

  constructor(name: string) {
    this.name = name;
  }

  next(state: UIState, type: TransitionType, testid: string = '', duration: number = 0) {
    this.transitions.push(new Transition(this, state, type, testid, duration));
  }

  getNext(name: string): Transition | undefined {
    for (let i = 0; i < this.transitions.length; i++) {
      if (this.transitions[i].to.name == name) {
        return this.transitions[i];
      }
    }
    return undefined;
  }

  getTransitions(): Transition[] {
    return this.transitions;
  }
}

export class Transition {
  from: UIState;
  to: UIState;
  type: TransitionType;
  duration: number;
  testid: string;

  constructor(from: UIState, to: UIState, type: TransitionType, testid: string, duration: number) {
    this.from = from;
    this.to = to;
    this.type = type;
    this.duration = duration;
    this.testid = testid;
  }

  getKey(postfix?: string): string {
    if (postfix) {
      return `${this.from.name}-${this.to.name}-${postfix}`;
    } else {
      return `${this.from.name}-${this.to.name}`;
    }
  }
}

// Deprecated
export abstract class StateMachine<T> {
  private _state: BehaviorSubject<UIState>;
  private _initState: UIState;
  private subject: BehaviorSubject<T>;
  render: BehaviorSubject<number>;
  hash: string;

  constructor() {
    this.define();
    this._initState = this.graph();
    this._state = new BehaviorSubject(this._initState);
    this.subject = new BehaviorSubject(this.initData());
    this.render = new BehaviorSubject(0);
    this.hash = getRandomString(10);
  }

  abstract define(): void;
  abstract graph(): UIState;
  abstract initData(): T;

  reset() {
    this._state.next(this._initState);
    this.subject.next(this.initData());
  }

  transition(name: string, model?: T) {
    const nextTransition = this._state.value.getNext(name);
    if (nextTransition) {
      this._state.next(nextTransition.to);
      if (model) {
        this.subject.next(model);
      }
    } else {
      console.trace('invalid transition :', this._state.value, name, nextTransition);
    }
  }

  getTestCase() {
    const graph = Graph.parse(this._initState);
    return graph.getTestCase();
  }

  getInitState(): UIState {
    return this._initState;
  }

  getCurrentState(): UIState {
    return this._state.value;
  }

  getStateSubject() {
    return this._state;
  }

  getDataSubject() {
    return this.subject;
  }

  setDataSubject(subject: BehaviorSubject<T>) {
    this.subject = subject;
  }

  getData() {
    return this.subject.value;
  }

  is(name: string): boolean {
    return this._state.value.name === name;
  }

  update(data: T) {
    // console.trace('update')
    this.subject.next(data);
  }
}

export function useStateChanged<T>(machine: StateMachine<T>, callback: (state: UIState) => void) {
  useEffect(() => {
    const sub = machine.getStateSubject().subscribe((st) => {
      callback(st);
    });
    return () => {
      sub.unsubscribe();
    };
  }, [machine]);
}

export function useMachine<T extends StateMachine<any>>(
  Machine: new () => T,
  onReady: (machine: T) => void | (() => void),
  onStateChanged: (state: UIState) => void,
) {
  const machine = useRef(new Machine()).current;
  useObservable(machine.render);
  useEffect(() => {
    const destructor = onReady(machine);
    const subList = [
      machine.getStateSubject().subscribe((st) => {
        onStateChanged(st);
        machine.render.next(new Date().getTime());
      }),
      machine.getDataSubject().subscribe((d) => {
        machine.render.next(new Date().getTime());
      }),
    ];
    return () => {
      subList.forEach((sub) => sub.unsubscribe());
      if (destructor) destructor();
    };
  }, [machine]);
  return machine;
}

export class Graph {
  private state: UIState;
  private next: Graph[] = [];
  private prev: Graph | undefined = undefined;

  constructor(state: UIState) {
    this.state = state;
    this.next = [];
    this.prev = undefined;
  }

  static parse(state: UIState): Graph {
    const graph = new Graph(state);
    graph.extend();
    return graph;
  }

  extend() {
    if (this.isCycle()) return;
    this.state.getTransitions().forEach((transition) => {
      const nextGraph = new Graph(transition.to);
      nextGraph.prev = this;
      this.next.push(nextGraph);
      nextGraph.extend();
    });
  }

  isCycle() {
    const nodes = this.getNodes();
    nodes.pop();
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i] == this.state.name) {
        return true;
      }
    }
    return false;
  }

  getNodes(): string[] {
    const nodes = this.prev?.getNodes() ?? [];
    nodes.push(this.state.name);
    return nodes;
  }

  getLeaves(): Graph[] {
    if (this.next.length === 0) {
      return [this];
    }
    const output: Graph[] = [];
    this.next.forEach((d) => {
      output.push(...d.getLeaves());
    });
    return output;
  }

  getPaths(): UIState[][] {
    const paths = this.prev?.getPaths() ?? [[]];
    paths.forEach((path) => {
      path.push(this.state);
    });
    return paths;
  }

  getTestCase(): TestCase[] {
    const leaves = this.getLeaves();
    const testCase: TestCase[] = [];
    leaves.forEach((leaf) => {
      leaf.getPaths().forEach((path) => {
        testCase.push(new TestCase(path));
      });
    });
    return testCase;
  }
}

class TestCase {
  path: UIState[];
  constructor(path: UIState[]) {
    this.path = path;
  }

  getTitle(): string {
    return this.path.map((p) => p.name).join(' - ');
  }

  getSteps(): {
    state: UIState;
    next: string;
    transition: Transition | undefined;
  }[] {
    const output = [];
    for (let i = 0; i < this.path.length; i++) {
      const next = this.path.length - 1 > i ? this.path[i + 1].name : '';
      const transition = this.path[i]
        .getTransitions()
        .find((transition) => transition.to.name == next);
      output.push({
        state: this.path[i],
        transition,
        next,
      });
    }
    return output;
  }
}
